import { Button, Grid, Row } from 'react-bootstrap';
import { get, isEmpty, map } from 'lodash';
import { Field, InjectedFormProps } from 'redux-form';
import FieldFormControl from '../../../../../components/FieldFormControl';
import { isRequired } from '../../../../../util/validators';
import { revObjectOrder } from '../../../../../helpers/Objects';
import IconTravel from '../../../components/icon/IconTravel';
import { IDestination, IOptionMap, IProduct, IStateCities, TripType } from '../../../../../data/api/models';
import { useState } from 'react';
import { Collapsable, ICollapsableComponent, IQuoteActions, IQuoteState } from '../../../../../services/quote/models';
import { setCollapsable } from '../../../../../services/quote/reducer';
import { AdmiralCardV2 } from '../AdmiralCardV2';
import { ErrorDisplay } from '../../../../../components/FieldFormControl/ErrorDisplay';
import { SelectCoverDatesAndDestinations } from './SelectCoverDatesAndDestinations';
import { MaxTripDurationSelector } from '../MaxTripDurationSelector';
import TripOptions from './TripOptions/trip-options';
import { addYears, format, parse, subDays } from 'date-fns';
import { FormHelper } from '../../../../../helpers/form-helper';
import React = require('react');

interface IChoseYourCoverProps extends InjectedFormProps {
  isRenewal: boolean;
  product: IProduct;
  selection: TripType;
  onResetForm: () => void;
  destinationsValue: any[];
  stateCitiesValue: any[];
  stateCities: IStateCities[];
  destinations: IDestination[];
  tripType: TripType;
  startDate: string;
  endDate: string;
  region: string;
  dispatch?: any;
  collapsable?: ICollapsableComponent;
  isAnnual?: boolean;
  quoteState: IQuoteState;
  options: IOptionMap,
  maxTripDuration?: number,

  change(field: string, value: any): void;

  renderDefinition(name: string): any;

  actions?: {
    quote: IQuoteActions;
  }
}

export const ChooseYourCover = ({
                                  isRenewal,
                                  product,
                                  selection,
                                  renderDefinition,
                                  onResetForm,
                                  tripType,
                                  startDate,
                                  endDate,
                                  region,
                                  stateCities,
                                  destinationsValue,
                                  stateCitiesValue,
                                  destinations,
                                  dispatch,
                                  collapsable,
                                  isAnnual,
                                  quoteState,
                                  options,
                                  change,
                                  maxTripDuration,
                                  actions,
                                  ...props
                                }: IChoseYourCoverProps) => {
  const [showContents, setShowContents] = useState(false);

  const onChangeCollapse = () => {
    dispatch(setCollapsable(null, Collapsable.COVER_LEVEL));
    onResetForm();
  }

  const tripTypeButtons = () => {
    if (isRenewal) {
      return null;
    }

    const tripType = get(product, 'metadata.trip_type', {})
    const reversedObject = revObjectOrder(tripType)

    return map(reversedObject, (name, key) => {
      const isSelected = selection === key;
      const subtext = name === 'Single Trip' ? 'Cover for one off trip to single or multiple destinations, and your return home' : 'Cover for multiple short-duration trips of up to 31 days throughout the year'
      return (
        <Row key={key} className="cyc--trip-type-container">
          <div className={isSelected ? 'box selected-trip-type' : 'box'}>
            <Field
              name="tripType"
              id={key + '-btn'}
              type="radio"
              value={key}
              component={FieldFormControl}
              onChange={openContents}
              validate={[isRequired]}
              validationMessage={'single'}
            >
              <div className={`trip-type-btn ${key}`}>
                <IconTravel type={key}/>
                <div className={'trip-type-selection-container'}>
                  <div className={'title'}>{name}</div>
                  <div className={'sub-text'}>{subtext}</div>
                </div>
              </div>
            </Field>
          </div>
        </Row>
      );
    });
  }

  const applyChange = (formValues) => {
    setShowContents(false);
    dispatch(setCollapsable(Collapsable.COVER_LEVEL, Collapsable.WHO_IS_TRAVELLING));
  }

  const canSave = (): boolean => {
    const WS = get(options, 'winter_sports', undefined);
    const CC = get(options, 'cruise_cover', undefined);
    const isSetTripOptions = (WS === true || WS === false) && (CC === true || CC === false);

    if (tripType === 'single') {
      const destinationsToCheck = Array.isArray(destinationsValue) ? destinationsValue.filter(item => item) : [];
      return isSetTripOptions && startDate && endDate && !isEmpty(destinationsToCheck);
    }

    if (tripType === 'annual') {
      return isSetTripOptions && !!startDate && !!region && !!maxTripDuration;
    }
  }

  const renderContent = () => {
    return showContents && (<>

      {/* HEADER */}
      <Row className={'cyc--title'} style={{ margin: '15px -15px' }}>
        Trip Details:
      </Row>

      {/* Dates and Destination */}
      <Row>
        <SelectCoverDatesAndDestinations
          product={product}
          renderDefinition={renderDefinition}
          tripType={selection}
          destinations={destinations}
          region={region}
          stateCities={stateCities}
          startDate={startDate}
          endDate={endDate}
          destinationsValue={destinationsValue}
          stateCitiesValue={stateCitiesValue}
          actions={actions}
          {...props}
        />
      </Row>

      {/* Trip Length */}
      {isAnnual ? (<>
        <Row className={'cyc--title'} style={{ margin: '15px -15px' }}>
          What is your maximum trip length?
        </Row>
        <Row>
          <MaxTripDurationSelector
            product={product}
            quoteState={quoteState}
            {...props}
          />
        </Row>
      </>) : (
        FormHelper.disabledValidationField('maxTripDuration')
      )}

      {/* Trip Options */}
      <Row style={{ margin: '-15px' }}>
        <TripOptions
          options={options}
          product={product}
          change={change}
        />
      </Row>

      {/* SAVE & CANCEL */}
      <Row className={'cyc--continue-button-container'}>
        <Button
          bsSize={'small'}
          type="button"
          block={true}
          disabled={!canSave()}
          onClick={applyChange}
          className={'admiral-primary-btn'}
        >
          CONTINUE
        </Button>
      </Row>

    </>);
  }

  const openContents = () => {
    onResetForm()
    setShowContents(true)
  }

  const regionToDisplay = region === 'worldwide_excluding' ? 'Worldwide Excluding' : region;
  const destinationsDisplay = destinationsValue ? destinationsValue.filter(item => item) : [];
  const stateCitiesDisplay = stateCitiesValue ? stateCitiesValue.filter(item => item) : [];

  const noSelectionMessage = (<>
    <div>
      <ErrorDisplay error={'Please make a selection to continue quote.'}/>
    </div>
  </>);

  const infoOnCollapsed = startDate ? (
    <div>
      <div>{!isAnnual ? 'Single Trip' : 'Annual Multi-Trip'} Cover</div>
      <div>Start Date: {startDate}</div>
      <div>End Date: {!isAnnual ? endDate : (
        startDate ? format(addYears(subDays(parse(startDate, 'dd/MM/yyyy', new Date()), 1), 1), 'dd/MM/yyyy') : null // calculate endDate for annual
      )}</div>
      {/* Single */}
      {!isEmpty(destinationsDisplay) ? (<div>Destinations: {destinationsDisplay.toString()}</div>) : null}
      {!isEmpty(stateCitiesDisplay) ? (<div>States: {stateCitiesDisplay.toString()}</div>) : null}
      {/* Annual */}
      <div style={{ textTransform: 'capitalize' }}>{region ? 'Region: ' + regionToDisplay : null}</div>
      {isAnnual ? 'Maximum trip length: ' + maxTripDuration + ' days' : null}
      {/* OPTIONS */}
      <div>Winter Sports: {!!get(options, 'winter_sports', false) ? 'Yes' : 'No'}</div>
      <div>Cruise: {!!get(options, 'cruise_cover', false) ? 'Yes' : 'No'}</div>
    </div>
  ) : noSelectionMessage;

  return (<>
    <AdmiralCardV2
      title={collapsable?.coverLevel?.opened || !collapsable?.coverLevel?.isEditable ? 'Which type of cover do you need?' : 'Trip Details'}
      icon={'adm-icon-shield.svg'}
      collapsable={{ value: collapsable.coverLevel, onHandleChange: onChangeCollapse }}
      headerMessage={{
        showAfterTouched: true, onClosed: infoOnCollapsed
      }}
    >
      <Grid style={{ width: '100%' }}>
        {tripTypeButtons()}
        {renderContent()}
      </Grid>
    </AdmiralCardV2>
  </>);
}
